import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/AxiosClient";

export interface DetailedFeedbackPayload {
  agent_name: string;
  feedback: string;
  feedback_type: string;
  image?: File;
}

export interface DetailedFeedbackResponse {
  message: string;
  feedback_id?: string;
}

export const submitDetailedFeedback = createAsyncThunk(
  "feedback/submitDetailedFeedback",
  async (payload: DetailedFeedbackPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("agent_name", payload.agent_name);
      formData.append("feedback", payload.feedback);
      formData.append("feedback_type", payload.feedback_type);

      if (payload.image) {
        formData.append("image", payload.image);
      }

      const response = await axiosClient.post<DetailedFeedbackResponse>(
        "/detailed_feedback",
        formData,
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to submit feedback",
      );
    }
  },
);

interface FeedbackState {
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: FeedbackState = {
  loading: false,
  error: null,
  success: false,
};

const feedbackSlice = createSlice({
  name: "feedback",
  initialState,
  reducers: {
    resetFeedbackState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitDetailedFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(submitDetailedFeedback.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(submitDetailedFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to submit feedback";
        state.success = false;
      });
  },
});

export const { resetFeedbackState } = feedbackSlice.actions;
export default feedbackSlice.reducer;
